
import React, { useState, useEffect } from 'react';
import { User, UserRole, SystemSettings } from '../types';
import {
  LogOut, LayoutDashboard, Package, Users, FileText, Settings, Menu, X, CreditCard, PenTool, Globe, ChevronLeft, ChevronRight, Wallet, Plus, Building2, MessageCircle, Tag, WifiOff, Coins, List, Cpu, Sun, Moon
} from 'lucide-react';
import { translations, Language, Direction } from '../services/translations';
import { api } from '../services/api';

interface LayoutProps {
  children?: React.ReactNode;
  user: User;
  onLogout: () => void;
  activeView: string;
  onChangeView: (view: string) => void;
  lang: Language;
  setLang: (lang: Language) => void;
  onTopUp?: () => void;
  onNewCase?: () => void; // Added prop for New Case action
  isCollapsed?: boolean;
  onCollapse?: (collapsed: boolean) => void;
}

export const Layout = ({ children, user, onLogout, activeView, onChangeView, lang, setLang, onTopUp, onNewCase, isCollapsed: controlledIsCollapsed, onCollapse }: LayoutProps) => {
  if (!user) return null;

  const [isMobileMenuOpen, setIsMobileMenuOpen] = React.useState(false);
  const [localIsCollapsed, setLocalIsCollapsed] = useState(false);

  const isCollapsed = controlledIsCollapsed !== undefined ? controlledIsCollapsed : localIsCollapsed;
  const setIsCollapsed = (value: boolean) => {
    if (onCollapse) {
      onCollapse(value);
    } else {
      setLocalIsCollapsed(value);
    }
  };

  const [settings, setSettings] = useState<SystemSettings | null>(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  const t = translations[lang];
  const isRTL = lang === 'ar';

  const [theme, setTheme] = useState<'light' | 'dark'>(() => {
    // Default to Dark if no preference
    const saved = localStorage.getItem('theme');
    return (saved === 'light' || saved === 'dark') ? saved : 'dark';
  });

  useEffect(() => {
    api.getSettings().then(setSettings);

    // Theme Application
    const root = window.document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }
    localStorage.setItem('theme', theme);

    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, [theme]); // Re-run when theme changes

  const getNavItems = () => {
    // Common items
    const dashboardItem = { icon: <LayoutDashboard size={20} />, label: t.nav.dashboard, id: 'dashboard' }; // Maps to overview
    const settingsItem = { icon: <Settings size={20} />, label: t.nav.settings, id: 'settings' };

    let items = [];

    if (user.role === UserRole.DENTIST || user.role === UserRole.MODERATOR) {
      items = [
        dashboardItem,
        { icon: <Package size={20} />, label: t.nav.myOrders, id: 'orders' },
        { icon: <Tag size={20} />, label: t.nav.priceList, id: 'price_list' },
      ];

      const canSeeFinancials = user.role === UserRole.DENTIST || (user.role === UserRole.MODERATOR && user.canSeeFinancials);
      if (canSeeFinancials) {
        items.push({ icon: <Wallet size={20} />, label: t.nav.invoices, id: 'invoices' });
      }

      if (user.role === UserRole.DENTIST) {
        items.push({ icon: <Building2 size={20} />, label: t.nav.myClinic, id: 'clinic' });
      }

    } else if (user.role === UserRole.TECHNICIAN) {
      items = [
        dashboardItem,
        { icon: <PenTool size={20} />, label: t.nav.workbench, id: 'tasks' },
        { icon: <Package size={20} />, label: t.nav.queue, id: 'queue' },
      ];
    } else if (user.role === UserRole.ADMIN) {
      // Expanded Vertical Menu for Admin - Replaces Horizontal Tabs
      items = [
        { icon: <LayoutDashboard size={20} />, label: t.nav.overview, id: 'dashboard' },
        { icon: <FileText size={20} />, label: t.nav.allOrders, id: 'all_orders' },
        { icon: <Users size={20} />, label: t.nav.users, id: 'users' },
        { icon: <BanknoteIcon size={20} />, label: t.nav.financials, id: 'financials' },
        { icon: <Coins size={20} />, label: t.nav.designerRates, id: 'rates' },
        { icon: <List size={20} />, label: t.nav.servicesCatalog, id: 'services' },
        { icon: <Tag size={20} />, label: t.nav.pricePlans, id: 'price_plans' },
        { icon: <Cpu size={20} />, label: t.nav.millingCenters, id: 'milling' },
        { icon: <Wallet size={20} />, label: t.nav.topUpRequests, id: 'requests' },
      ];
    }

    return [...items, settingsItem];
  };

  // Helper component for icons used above but not imported directly in body
  const BanknoteIcon = ({ size }: { size: number }) => <CreditCard size={size} />;

  const toggleLang = () => {
    setLang(lang === 'en' ? 'ar' : 'en');
  };

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const AppLogo = ({ className = "h-12", collapsed = false }) => (
    <div className={`bg-surface rounded-lg shadow-subtle flex items-center justify-center transition-all overflow-hidden ${collapsed ? 'w-10 h-10' : 'p-0'}`}>
      {theme === 'dark' ? (
        <img src="https://pqcchblhugugkywsfofz.supabase.co/storage/v1/object/public/meida/logo.png" alt="Galal Dental" className={`${className} w-auto object-contain p-1`} />
      ) : settings?.logoUrl ? (
        <img src={settings.logoUrl} alt="Logo" className={`${className} w-auto object-cover block`} />
      ) : (
        <div className="w-10 h-10 flex items-center justify-center">
          <span className="text-primary font-bold text-xl">G</span>
        </div>
      )}
    </div>
  );

  const canSeeBalance = user.role === UserRole.DENTIST || (user.role === UserRole.MODERATOR && user.canSeeFinancials);

  return (
    <div dir={Direction[lang]} className={`min-h-screen bg-background text-primary flex overflow-hidden ${isRTL ? 'font-arabic' : 'font-sans'}`}>
      {/* Sidebar */}
      <aside
        className={`fixed left-0 top-0 h-screen bg-surface border-r border-border transition-all duration-300 z-40 flex flex-col
        ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
        ${isCollapsed ? 'w-20' : 'w-64'}
      `}>
        <div className="p-4 border-b border-border flex items-center justify-between h-16">
          {!isCollapsed && (
            <div className="flex items-center gap-2 overflow-hidden">
              <AppLogo className="h-8" />
              <span className="font-bold text-xl truncate text-primary">Galal Lab</span>
            </div>
          )}
          {isCollapsed && (
            <div className="w-full flex justify-center">
              <AppLogo className="h-8" collapsed />
            </div>
          )}
          <button onClick={() => setIsCollapsed(!isCollapsed)} className="hidden md:block text-secondary hover:text-primary">
            {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
          </button>
          <button onClick={() => setIsMobileMenuOpen(false)} className="md:hidden text-secondary">
            <X size={24} />
          </button>
        </div>

        {/* Primary Action for Dentists */}
        {(user.role === UserRole.DENTIST || user.role === UserRole.MODERATOR || user.role === UserRole.ADMIN) && onNewCase && (
          <div className={`px-4 mb-4 ${isCollapsed ? 'flex justify-center' : ''}`}>
            <button
              onClick={onNewCase}
              className={`bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl shadow-lg shadow-blue-900/20 transition-all flex items-center justify-center gap-2
                    ${isCollapsed ? 'w-10 h-10 p-0' : 'w-full py-3.5'}
                    `}
              title={t.dashboard.newCase}
            >
              <Plus size={20} />
              {!isCollapsed && <span>{t.dashboard.newCase}</span>}
            </button>
          </div>
        )}

        {/* Navigation Items - Scrollable */}
        <nav className="flex-1 px-4 space-y-1 py-4 overflow-y-auto custom-scrollbar">
          {getNavItems().map((item) => (
            <button
              key={item.id}
              onClick={() => onChangeView(item.id)}
              title={isCollapsed ? item.label : ''}
              className={`w-full flex items-center gap-3 px-3 py-3 text-sm font-medium rounded-xl transition-all duration-200 group relative overflow-hidden shrink-0
                ${activeView === item.id
                  ? 'bg-surfaceHighlight text-primary shadow-sm border border-border'
                  : 'text-secondary hover:bg-surfaceHighlight/50 hover:text-primary'}
                ${isCollapsed ? 'justify-center' : ''}  
              `}
            >
              <span className={`${activeView === item.id ? 'text-primary' : 'text-secondary group-hover:text-primary transition-colors'}`}>
                {item.icon}
              </span>
              {!isCollapsed && <span className="animate-fade-in whitespace-nowrap text-left flex-1 truncate">{item.label}</span>}

              {activeView === item.id && !isCollapsed && <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-l-full"></div>}
            </button>
          ))}
        </nav>

        {/* Footer Area */}
        <div className="p-4 border-t border-border bg-surfaceHighlight/30 shrink-0">
          <a
            href="https://wa.me/201556761102"
            target="_blank"
            rel="noreferrer"
            className={`w-full mb-2 flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium text-[#25D366] hover:bg-[#25D366]/10 transition-colors border border-transparent ${isCollapsed ? 'justify-center' : ''}`}
            title={t.nav.support}
          >
            <MessageCircle size={18} />
            {!isCollapsed && t.nav.support}
          </a>

          {/* Theme Toggle */}
          <button
            onClick={toggleTheme}
            className={`w-full mb-2 flex items-center gap-2 px-3 py-2 rounded-lg bg-surface border border-border text-xs text-secondary hover:text-primary hover:border-secondary transition-all shadow-sm ${isCollapsed ? 'justify-center' : ''}`}
            title="Toggle Theme"
          >
            {theme === 'dark' ? <Moon size={14} /> : <Sun size={14} />}
            {!isCollapsed && <span>{theme === 'dark' ? t.common.darkMode : t.common.lightMode}</span>}
          </button>

          <button
            onClick={toggleLang}
            className={`w-full mb-3 flex items-center gap-2 px-3 py-2 rounded-lg bg-surface border border-border text-xs text-secondary hover:text-primary hover:border-secondary transition-all shadow-sm ${isCollapsed ? 'justify-center' : ''}`}
            title="Switch Language"
          >
            <Globe size={14} />
            {!isCollapsed && <span>{lang === 'en' ? 'العربية' : 'English'}</span>}
          </button>

          <div className={`flex items-center gap-3 px-1 mb-3 ${isCollapsed ? 'justify-center' : ''}`}>
            <div className="w-9 h-9 min-w-[36px] rounded-full bg-surfaceHighlight flex items-center justify-center text-sm font-bold text-secondary border border-border">
              {user.name.charAt(0)}
            </div>
            {!isCollapsed && (
              <div className="flex-1 overflow-hidden animate-fade-in">
                <p className="text-sm font-medium truncate text-primary">{user.name}</p>
                <p className="text-xs text-secondary truncate capitalize">
                  {user.role === UserRole.MODERATOR ? t.common.staff : user.role.toLowerCase()}
                </p>
              </div>
            )}
          </div>
          <button
            onClick={onLogout}
            className={`w-full flex items-center gap-2 px-3 py-2 text-sm font-medium text-secondary hover:text-red-400 hover:bg-red-900/10 rounded-lg transition-colors border border-transparent ${isCollapsed ? 'justify-center' : ''}`}
            title={t.nav.logout}
          >
            <LogOut size={16} />
            {!isCollapsed && t.nav.logout}
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="md:hidden fixed top-0 w-full bg-surface/80 backdrop-blur-md border-b border-border z-50 flex justify-between items-center px-4 pt-safe-top pb-3 h-16 shadow-lg">
        <div className="flex items-center gap-3">
          <AppLogo className="h-8" />
          <span className="font-bold text-primary text-lg">Galal Dental</span>
        </div>
        <div className="flex items-center gap-3">
          {canSeeBalance && (
            <div className="px-3 py-1.5 bg-surface border border-border rounded-full flex items-center gap-2">
              <span className={`text-sm font-bold ${user.balance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                {user.balance.toLocaleString()}
              </span>
            </div>
          )}
          <button onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} className="text-secondary hover:text-primary p-2">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="md:hidden fixed inset-0 bg-background/95 backdrop-blur-xl z-40 pt-20 px-6 animate-fade-in flex flex-col h-full safe-area-bottom">
          <div className="pb-6 border-b border-border mb-6">
            {(user.role === UserRole.DENTIST || user.role === UserRole.MODERATOR || user.role === UserRole.ADMIN) && onNewCase && (
              <button
                onClick={() => { onNewCase(); setIsMobileMenuOpen(false); }}
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-bold rounded-xl py-4 shadow-lg shadow-blue-900/20 transition-all flex items-center justify-center gap-3"
              >
                <Plus size={20} />
                {t.dashboard.newCase}
              </button>
            )}
          </div>

          <nav className="space-y-2 flex-1 overflow-y-auto pb-8">
            {getNavItems().map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  onChangeView(item.id);
                  setIsMobileMenuOpen(false);
                }}
                className={`w-full flex items-center gap-4 px-6 py-4 text-base font-medium rounded-xl border transition-colors ${activeView === item.id ? 'bg-surfaceHighlight border-primary text-primary' : 'border-border bg-surface text-secondary hover:bg-surfaceHighlight'}`}
              >
                {item.icon}
                {item.label}
              </button>
            ))}

            <div className="pt-4 mt-4 border-t border-zinc-800">
              <a
                href="https://wa.me/201556761102"
                target="_blank"
                rel="noreferrer"
                className="w-full flex items-center gap-4 px-6 py-4 text-base font-medium rounded-xl border border-[#25D366]/30 text-[#25D366] bg-[#25D366]/5"
              >
                <MessageCircle size={20} />
                {t.nav.support}
              </a>

              <button
                onClick={() => { toggleLang(); setIsMobileMenuOpen(false); }}
                className="w-full flex items-center gap-4 px-6 py-4 text-base font-medium rounded-xl border border-border text-secondary mt-2 bg-surface"
              >
                <Globe size={20} />
                {t.common.switchLang}
              </button>
            </div>
          </nav>

          <div className="pb-8 pt-4 border-t border-zinc-800 shrink-0">
            <div className="flex items-center gap-3 px-2 mb-6">
              <div className="w-10 h-10 rounded-full bg-surfaceHighlight flex items-center justify-center text-lg font-bold text-secondary border border-border">
                {user.name.charAt(0)}
              </div>
              <div>
                <p className="text-base font-medium text-primary">{user.name}</p>
                <p className="text-sm text-secondary capitalize">{user.role === UserRole.MODERATOR ? t.common.staff : user.role.toLowerCase()}</p>
              </div>
            </div>
            <button
              onClick={onLogout}
              className="w-full flex items-center justify-center gap-2 px-6 py-4 text-lg font-medium text-red-400 rounded-xl hover:bg-red-900/20 border border-red-900/30 bg-red-900/10"
            >
              <LogOut size={20} />
              {t.nav.logout}
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main
        className={`flex-1 px-4 pt-20 md:p-10 md:pt-10 transition-all duration-300 overflow-y-auto h-screen bg-background relative scroll-smooth
        ${isRTL ? (isCollapsed ? 'md:mr-20' : 'md:mr-72') : (isCollapsed ? 'md:ml-20' : 'md:ml-72')}
        `}
      >
        {!isOnline && (
          <div className="bg-red-500/10 border border-red-500/20 text-red-400 px-4 py-2 rounded-lg mb-4 flex items-center gap-2 animate-pulse sticky top-0 z-30 backdrop-blur">
            <WifiOff size={16} />
            <span className="text-sm font-bold">{t.common.offline}</span>
          </div>
        )}

        <div className="relative z-10 max-w-7xl mx-auto pb-24 md:pb-20">
          {/* Header Bar with Balance (Desktop) */}
          <div className="hidden md:flex justify-between items-center mb-10">
            <div className="flex-1"></div>
            {canSeeBalance && (
              <div className="flex items-center gap-4 animate-fade-in">
                <div className="bg-surface border border-border rounded-full pl-5 pr-1 py-1 flex items-center gap-4 shadow-lg">
                  <div className="flex flex-col leading-none text-right">
                    <span className="text-[10px] text-secondary uppercase font-bold tracking-wider">{t.dashboard.balance}</span>
                    <span className={`text-lg font-bold ${user.balance < 0 ? 'text-red-400' : 'text-emerald-400'}`}>
                      {user.balance.toLocaleString()} {settings?.currency || 'EGP'}
                    </span>
                  </div>
                  <button
                    onClick={() => onTopUp ? onTopUp() : onChangeView('financials')}
                    className="w-8 h-8 rounded-full bg-primary text-background flex items-center justify-center hover:bg-secondary transition-colors shadow-glow"
                    title={t.dashboard.topUp}
                  >
                    <Plus size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>

          {children}
        </div>
      </main>
    </div>
  );
};
